import React, { useState } from 'react';
import { Download, FileText, Calendar, Search } from 'lucide-react';

const Downloads = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  const downloads = [
    {
      id: 1,
      title: 'Q4 FY2025 Market Analysis & Outlook',
      type: 'Research Report',
      date: '15 Jan 2025',
      size: '2.5 MB',
      downloads: 1,
      category: 'report'
    },
    {
      id: 2,
      title: 'Technology Sector Deep Dive',
      type: 'Sector Analysis',
      date: '12 Jan 2025',
      size: '3.2 MB',
      downloads: 2,
      category: 'report'
    },
    {
      id: 3,
      title: 'Reliance Industries - Research Report',
      type: 'Stock Report',
      date: '15 Jan 2025',
      size: '1.8 MB',
      downloads: 1,
      category: 'stock'
    },
    {
      id: 4,
      title: 'TCS - Detailed Analysis',
      type: 'Stock Report',
      date: '12 Jan 2025',
      size: '1.5 MB',
      downloads: 1,
      category: 'stock'
    },
    {
      id: 5,
      title: 'Portfolio Performance Report - December 2024',
      type: 'Portfolio Report',
      date: '31 Dec 2024',
      size: '890 KB',
      downloads: 1,
      category: 'portfolio'
    },
    {
      id: 6,
      title: 'Top 10 Stock Picks for 2025',
      type: 'Stock Ideas',
      date: '10 Jan 2025',
      size: '2.1 MB',
      downloads: 3,
      category: 'report'
    },
    {
      id: 7,
      title: 'Banking Sector Analysis',
      type: 'Sector Analysis',
      date: '8 Jan 2025',
      size: '2.8 MB',
      downloads: 1,
      category: 'report'
    },
    {
      id: 8,
      title: 'HDFC Bank - Buy Recommendation',
      type: 'Stock Report',
      date: '10 Jan 2025',
      size: '1.6 MB',
      downloads: 2,
      category: 'stock'
    }
  ];

  const filteredDownloads = downloads
    .filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = filter === 'all' || item.category === filter;
      return matchesSearch && matchesFilter;
    });

  const totalSize = downloads.reduce((acc, item) => {
    const sizeInMB = item.size.includes('MB') 
      ? parseFloat(item.size) 
      : parseFloat(item.size) / 1024;
    return acc + sizeInMB;
  }, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Downloads</h2>
        <p className="text-gray-600">Access and manage all your downloaded files</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <FileText className="text-blue-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 font-medium">Total Downloads</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">{downloads.length}</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-green-100 rounded-lg">
              <Download className="text-green-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 font-medium">This Month</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">6</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FileText className="text-purple-600" size={24} />
            </div>
            <p className="text-sm text-gray-600 font-medium">Total Size</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">{totalSize.toFixed(1)} MB</p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search downloads..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-yellow-400"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'all' ? 'bg-yellow-400 text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('report')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'report' ? 'bg-yellow-400 text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Reports
            </button>
            <button
              onClick={() => setFilter('stock')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'stock' ? 'bg-yellow-400 text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Stocks
            </button>
            <button
              onClick={() => setFilter('portfolio')}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filter === 'portfolio' ? 'bg-yellow-400 text-gray-900' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              Portfolio
            </button>
          </div>
        </div>
      </div>

      {/* Downloads List */}
      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">File Name</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Type</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Size</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Downloads</th>
                <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredDownloads.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <FileText className="text-blue-600" size={20} />
                      </div>
                      <p className="font-semibold text-gray-900">{item.title}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-block bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">
                      {item.type}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <Calendar size={14} />
                      {item.date}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{item.size}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{item.downloads}x</td>
                  <td className="px-6 py-4">
                    <button className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors text-sm font-semibold">
                      <Download size={16} />
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredDownloads.length === 0 && (
          <div className="text-center py-12">
            <FileText className="mx-auto text-gray-300 mb-4" size={64} />
            <p className="text-gray-500 text-lg">No downloads found</p>
            <p className="text-gray-400 text-sm">Try adjusting your search or filter</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Downloads;
